import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Strings from '../../../Strings';
import { actions } from '../redux/actions';
import { GET_BLOCKING_ENROLLMENTS_RESULT, GET_CARE_PROVIDERS_RESULT, LINK_ORGANIZATION_ERROR, ORGANIZATION_LIST_RESULT, UNLINK_ORGANIZATION_ERROR } from '../redux/constants';
import { notificationActions } from '../../../components/Notification/redux/actions';
import UserList from '../../Users/UserList';
import { CARE_PROVIDER, DOCTOR } from '../../../utils/userRoles';
import { Input, SelectField } from '../../../containers/Form';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';


const getErrorMessage = response => response?.response?.data?.standard_error?.message || response?.response?.data?.error?.message || 'Operation failed';

const createAddDraft = () => ({value: null, label: Strings.none, external_api_enabled: true, show_all_patients: false});
const createSettingsDraft = provider => ({
  external_api_enabled: Boolean(provider?.external_api_enabled),
  show_all_patients: Boolean(provider?.show_all_patients),
});

const hasSettingsChanged = (provider, settings) => (
  Boolean(provider?.external_api_enabled) !== Boolean(settings?.external_api_enabled)
  || Boolean(provider?.show_all_patients) !== Boolean(settings?.show_all_patients)
);

function OrgCareProviders(props) {
  const {
    organizationId,
    getCareProviders,
    getOrgs,
    linkOrganization,
    unlinkOrganization,
    getBlockingEnrollmentsForProvider,
    showNotification,
  } = props;
  const [orgs, setOrgs] = React.useState([]);
  const [linkedOrgs, setLinkedOrgs] = React.useState([]);
  const [selectedOrg, setSelectedOrg] = React.useState(createAddDraft());
  const [providerSettings, setProviderSettings] = React.useState(createSettingsDraft());
  const [blockingEnrollments, setBlockingEnrollments] = React.useState([]);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [removeModalOpen, setRemoveModalOpen] = React.useState(false);
  const [refreshTimestamp, setRefreshTimestamp] = React.useState(Date.now());
  const activeProvider = linkedOrgs.find(provider => !provider?.provider_disengaged);
  const deletedProvider = linkedOrgs.find(provider => provider?.provider_disengaged);
  const currentProvider = activeProvider || deletedProvider;

  const getLinkedOrgsAndProviders = () => {
    getCareProviders().then(resp => {
      if (resp?.type === GET_CARE_PROVIDERS_RESULT) {
        setLinkedOrgs(resp.response);
        setSelectedOrg(createAddDraft());
      }
    });
    setRefreshTimestamp(Date.now());
  };

  const getBlockingEnrollments = providerOrgId => {
    getBlockingEnrollmentsForProvider(organizationId, providerOrgId).then(resp => {
      if (resp?.type === GET_BLOCKING_ENROLLMENTS_RESULT) {
        setBlockingEnrollments(resp.response);
      }
    });
  };

  React.useEffect(() => {
    if (currentProvider?.uuid) {
      getBlockingEnrollments(currentProvider.uuid);
    } else {
      setBlockingEnrollments([]);
    }
  }, [currentProvider]);

  React.useEffect(() => {
    setProviderSettings(createSettingsDraft(activeProvider));
  }, [activeProvider]);

  React.useEffect(() => {
    getOrgs('care-provider').then(resp => {
      if (resp?.type === ORGANIZATION_LIST_RESULT) {
        setOrgs(
          resp.response?.data?.map(o => {
            return { ...o, value: o.masked_id, label: o.name };
          }),
        );
      }
    });
    getLinkedOrgsAndProviders();
  }, []);

  const openAddModal = () => {
    setSelectedOrg(createAddDraft());
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setSelectedOrg(createAddDraft());
  };

  const openRemoveModal = () => {
    if (!currentProvider) {
      return;
    }
    setRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    setRemoveModalOpen(false);
  };

  const onAdd = async () => {
    if (!selectedOrg?.value) {
      return;
    }

    const response = await linkOrganization({
      provider_org_uuid: selectedOrg.value,
      external_api_enabled: selectedOrg.external_api_enabled,
      show_all_patients: selectedOrg.show_all_patients,
    });

    if (response && response.type === LINK_ORGANIZATION_ERROR) {
      showNotification(getErrorMessage(response), 5000, true);
      return;
    }

    closeAddModal();
    getLinkedOrgsAndProviders();
  };

  const onSaveSettings = async () => {
    if (!activeProvider) {
      return;
    }

    const response = await linkOrganization({
      provider_org_uuid: activeProvider.uuid,
      external_api_enabled: providerSettings.external_api_enabled,
      show_all_patients: providerSettings.show_all_patients,
    });

    if (response && response.type === LINK_ORGANIZATION_ERROR) {
      showNotification(getErrorMessage(response), 5000, true);
      return;
    }

    getLinkedOrgsAndProviders();
  };

  const onRemove = async (force = false) => {
    if (!currentProvider) {
      return;
    }

    const response = await unlinkOrganization(currentProvider.uuid, { force_unassign_from_enrollments: force || undefined });
    if (response && response.type === UNLINK_ORGANIZATION_ERROR) {
      closeRemoveModal();
      showNotification(getErrorMessage(response), 5000, true);
      return;
    }

    closeRemoveModal();
    getLinkedOrgsAndProviders();
  };

  const onOrgSelected = option => {
    if (!option?.value) {
      setSelectedOrg(createAddDraft());
      return;
    }

    setSelectedOrg({...option, external_api_enabled: true, show_all_patients: false});
  };

  return (
    <>
      <div className="rjsf">
        <fieldset>
          <div className={`form-group field field-object ${currentProvider ? 'leaf' : ''}`}>
            <fieldset>
              <header className="header-with-button">
                {Strings.careProviderOrganization}
                <div className="buttons">
                  {hasSettingsChanged(activeProvider, providerSettings) && (
                    <Button
                      class={HEADER_BUTTON_DARK_BLUE}
                      onClick={onSaveSettings}
                      title={Strings.saveSettings}
                    />
                  )}
                  {currentProvider ? (
                    <Button class="remove" onClick={openRemoveModal} title={Strings.remove} />
                  ) : (
                    <Button class={HEADER_BUTTON_DARK_BLUE} onClick={openAddModal} title={Strings.add} />
                  )}
                </div>
              </header>
              {currentProvider ? (
                <>
                  <div>{currentProvider.name}</div>
                  {activeProvider && (
                    <>
                      <Input
                        type="checkbox"
                        name="current_external_api_enabled"
                        label={Strings.externalApiEnabled}
                        checked={providerSettings.external_api_enabled}
                        onChange={e => setProviderSettings({...providerSettings, external_api_enabled: e.target.checked})}
                        rowClassName="checkbox-first"
                      />
                      <Input
                        type="checkbox"
                        name="current_show_all_patients"
                        label={Strings.showAllPatients}
                        checked={providerSettings.show_all_patients}
                        onChange={e => setProviderSettings({...providerSettings, show_all_patients: e.target.checked})}
                        rowClassName="checkbox-first"
                      />
                    </>
                  )}
                </>
              ) : (
                <span>No care provider organization configured.</span>
              )}
              {deletedProvider && (
                <div className="field-array">
                  <div className="blocking-enrollments-warning">
                    <div className="icon-container">
                      <div className="warning-icon"></div>
                    </div>
                    <h4>{Strings.inactiveCareProviderWarning}</h4>
                  </div>
                  <div>
                    <strong>{deletedProvider.name}</strong>: {Strings.inactiveCareProviderWarningDetails}
                  </div>
                </div>
              )}
            </fieldset>
          </div>
        </fieldset>
      </div>
      {addModalOpen && (
        <SimpleModal
          name="add-care-provider-organization-modal"
          title="Add care provider organization"
          onCancel={closeAddModal}
          onSubmit={onAdd}
          isSubmitDisabled={!selectedOrg?.value}
          content={(
            <>
              <SelectField
                label={Strings.careProviderOrganization}
                data={[{value: null, label: Strings.none}].concat(orgs || [])}
                value={selectedOrg?.value}
                onChange={onOrgSelected}
                isSearchable
              />
              <Input
                type="checkbox"
                name="external_api_enabled"
                label={Strings.externalApiEnabled}
                checked={selectedOrg?.external_api_enabled}
                onChange={e => setSelectedOrg({...selectedOrg, external_api_enabled: e.target.checked})}
                rowClassName="checkbox-first"
              />
              <Input
                type="checkbox"
                name="show_all_patients"
                label={Strings.showAllPatients}
                checked={selectedOrg?.show_all_patients}
                onChange={e => setSelectedOrg({...selectedOrg, show_all_patients: e.target.checked})}
                rowClassName="checkbox-first"
              />
            </>
          )}
        />
      )}
      {removeModalOpen && currentProvider && (
        <SimpleModal
          name="remove-care-provider-organization-modal"
          title="Remove care provider organization"
          onCancel={closeRemoveModal}
          onSubmit={onRemove}
          content={(
            <>
              {Strings.formatString(Strings.removeCareProviderWarning, currentProvider.name)}
              {blockingEnrollments.length > 0 && (
                <>
                  <div className="blocking-enrollments-warning">
                    <div className="icon-container">
                      <div className="warning-icon"></div>
                    </div>
                    <h4>{Strings.careProviderAssignmentWarning}</h4>
                  </div>
                  {Strings.careProviderAssignmentWarningDetails}
                  <ul>
                    {blockingEnrollments.map(item => {
                      const assignments = [
                        item.assigned_care_provider_name ? `provider: ${item.assigned_care_provider_name}` : null,
                        item.assigned_care_provider_org_name ? `organization: ${item.assigned_care_provider_org_name}` : null,
                      ].filter(Boolean).join(', ');
                      const program = item.program ? ` (${item.program})` : '';
                      const suffix = assignments ? ` [${assignments}]` : '';

                      return (
                        <li key={item.enrollment_uuid}>
                          <strong>{item.patient_name}</strong>
                          {`: ${item.enrollment_uuid}${program}${suffix}`}
                        </li>
                      );
                    })}
                    {/* {hiddenCount > 0 && (
                      <li>
                        {`and ${hiddenCount} more`}
                      </li>
                    )} */}
                  </ul>
                </>
              )}
            </>
          )}
          customButtons={(
            <div className="buttons" key="button-bar">
              <div className="bottom-button-container">
                <button className="brand-white-gray" key="cancel" type="cancel" onClick={closeRemoveModal}>
                  {Strings.cancel}
                </button>
              </div>
              <div className="bottom-button-container">
                <button className="remove" key="submit" type="button" onClick={() => onRemove(blockingEnrollments.length > 0)}>
                  {blockingEnrollments.length > 0 ? Strings.unassignAndRemove : Strings.remove}
                </button>
              </div>
            </div>
          )}
        />
      )}
      <UserList
        organizationId={organizationId}
        role={DOCTOR}
        type={CARE_PROVIDER}
        headerLabel={Strings.careProvidersHeader}
        refreshTimestamp={refreshTimestamp} 
      />
    </>
  );
}

OrgCareProviders.propTypes = {
  organizationId: PropTypes.any,
  getCareProviders: PropTypes.func,
  getOrgs: PropTypes.func,
  linkOrganization: PropTypes.func,
  unlinkOrganization: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapStateToProps = () => {
  return {
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getCareProviders: () => dispatch(actions.getCareProviders({orgId: ownProps.organizationId})),
  linkOrganization: request => dispatch(actions.linkOrganization(ownProps.organizationId, request)),
  unlinkOrganization: (linkedOrgId, request) => dispatch(actions.unlinkOrganization(ownProps.organizationId, linkedOrgId, request)),
  getBlockingEnrollmentsForProvider: (orgId, providerOrgId) => dispatch(actions.getBlockingEnrollments(orgId, providerOrgId)),
  getOrgs: type => dispatch(actions.getOrgs(type)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrgCareProviders);
